/**
 * Roster templates — the three shapes a campaign staff actually comes in.
 *
 * The Roster step used to offer exactly one affordance: "Add person", one
 * blank row at a time. Every campaign then rebuilt the same three or four
 * combinations by hand, and the combinations are not folklore — they are
 * written down in `docs-site/.../campaign-onboarding.mdx` under step 3
 * ("Common combinations"). This module encodes that list so the wizard can
 * seed it instead of asking a first-time provisioner to remember it.
 *
 * Three rules constrain what a template is allowed to do, and each one exists
 * because breaking it manufactures a preflight BLOCK the person cannot see
 * until the Launch step:
 *
 * 1. **Never seed a role the provisioner cannot grant.** `/admin/users/role-options`
 *    already answers "what may I hand out?" against the grant matrix. A
 *    template that ignores it produces `ROLE_NOT_GRANTABLE` per row. So a slot
 *    whose role is not grantable is DROPPED (or demoted to a documented alias,
 *    see `fallbackRoles`) and reported back for the UI to say so out loud.
 *
 * 2. **Check seats before seeding, not after.** `SEATS_EXHAUSTED` fires when
 *    the roster outruns `tenant_entitlements`. Ten rows on a five-seat tenant
 *    is a block discovered three steps later, so the shortfall is computed
 *    here — at selection time — against `/admin/seats`.
 *
 * 3. **Roles and scopes only. Never emails.** An email is a fact about a
 *    person; inventing one produces `PLACEHOLDER_EMAIL` at best and a real
 *    stranger's account at worst. Every seeded row has an empty email for a
 *    human to fill in.
 *
 * The scope rule deserves its own note, because the default is a trap: in this
 * system **an empty scope list means TENANT-GLOBAL, not "no access"** — the
 * whole county, every other candidate's numbers included. So no template ever
 * seeds a staff row with an empty scope; each slot declares whether it takes
 * the campaign's whole reach or one slice of it, and both are derived from the
 * candidate's own reach so `scope_is_subset` holds by construction.
 *
 * The turf catalog is an ARGUMENT here, never an import. It used to be
 * `MAP_REGIONS`, a hardcoded set of Platte County subregions in the bundle,
 * which meant a template on any other tenant sliced turf out of a county the
 * campaign has never heard of and wrote those precinct numbers into
 * `user_scopes`. Turfs are per-tenant (`GET /geo/regions`), so they arrive
 * with the call; a caller with no catalog gets reach copies, not somebody
 * else's neighbourhoods.
 */
import type { LaunchRosterMember, RoleOption, SeatSnapshot } from '@/api/launch';
import type { MapRegionCatalog } from '@/api/geoRegions';
import type { UserScopeInput } from '@/types';

// ---------------------------------------------------------------------------
// Template shape
// ---------------------------------------------------------------------------

/**
 * How a slot gets its turf.
 *
 * - `campaign` — a copy of the candidate's reach. Equal is a subset, so this
 *   always passes containment. For a genuinely county-wide race (empty reach,
 *   confirmed) this copies the emptiness and the staffer ends up tenant-global
 *   like the candidate; preflight says so as a `TENANT_GLOBAL_STAFFER` warn,
 *   which is the correct outcome to surface rather than to hide.
 * - `slice` — ONE subregion inside the reach, handed out round-robin so two
 *   coordinators don't land on the same doors. Deliberately the narrowest
 *   thing that still lets the person work; widening a turf is one dropdown,
 *   un-leaking a county is not.
 */
export type ScopeStrategy = 'campaign' | 'slice';

export interface TemplateSlot {
  /** Wire value, lowercase — the same strings `/admin/users/role-options` serves. */
  role: string;
  /**
   * Roles to try, in order, when `role` is not grantable to this provisioner.
   *
   * Kept to DOCUMENTED ALIASES only. `manager` is the legacy name for
   * `org_manager` (see `backend/app/models/user.py`), so substituting it
   * changes the wire value and nothing else. Silently demoting a campaign
   * manager to a coordinator would change what the person can do while the
   * template still claimed to have staffed the job — better to drop the row
   * and say why.
   */
  fallbackRoles?: string[];
  relation: 'candidate' | 'staff';
  /** What this person is called on a campaign, for the UI and the skip notice. */
  label: string;
  count: number;
  /** Only the campaign manager gets this: they staff their own field team. */
  canProvision: boolean;
  scope: ScopeStrategy;
}

export interface RosterTemplate {
  id: RosterTemplateId;
  name: string;
  /** One line, in the wizard's voice. Shown on the button. */
  blurb: string;
  slots: TemplateSlot[];
}

export type RosterTemplateId = 'kitchen_table' | 'field_team' | 'full_bench';

// ---------------------------------------------------------------------------
// The three templates
// ---------------------------------------------------------------------------
//
// They are written as supersets of each other — Field Team is Kitchen Table
// plus staff, Full Bench is Field Team plus the specialists — because that is
// how the choice actually reads to someone picking one.

const KITCHEN_TABLE_SLOTS: TemplateSlot[] = [
  {
    role: 'candidate',
    relation: 'candidate',
    label: 'Candidate',
    count: 1,
    canProvision: false,
    // The wizard copies the campaign's reach onto the candidate itself, so a
    // candidate row carries no scopes of its own.
    scope: 'campaign',
  },
  {
    role: 'canvasser',
    relation: 'staff',
    label: 'Canvasser',
    count: 1,
    canProvision: false,
    scope: 'slice',
  },
];

const FIELD_TEAM_SLOTS: TemplateSlot[] = [
  KITCHEN_TABLE_SLOTS[0],
  {
    role: 'org_manager',
    fallbackRoles: ['manager'],
    relation: 'staff',
    label: 'Campaign manager',
    count: 1,
    // `can_provision` is the point of this row: the manager staffs their own
    // field team without anyone widening a role tenant-wide to do it.
    canProvision: true,
    scope: 'campaign',
  },
  {
    role: 'coordinator',
    relation: 'staff',
    label: 'Field organizer',
    count: 2,
    canProvision: false,
    scope: 'slice',
  },
  {
    role: 'canvasser',
    relation: 'staff',
    label: 'Canvasser',
    count: 3,
    canProvision: false,
    scope: 'slice',
  },
];

const FULL_BENCH_SLOTS: TemplateSlot[] = [
  ...FIELD_TEAM_SLOTS,
  {
    role: 'sms_manager',
    relation: 'staff',
    label: 'SMS lead',
    count: 1,
    canProvision: false,
    scope: 'campaign',
  },
  {
    role: 'messenger',
    relation: 'staff',
    label: 'Messenger',
    count: 1,
    canProvision: false,
    scope: 'slice',
  },
  {
    role: 'analyst',
    relation: 'staff',
    label: 'Analyst',
    count: 1,
    canProvision: false,
    scope: 'campaign',
  },
];

export const ROSTER_TEMPLATES: RosterTemplate[] = [
  {
    id: 'kitchen_table',
    name: 'Kitchen Table',
    blurb: 'The candidate and one person knocking. Add more later.',
    slots: KITCHEN_TABLE_SLOTS,
  },
  {
    id: 'field_team',
    name: 'Field Team',
    blurb: 'A campaign manager, two organizers, and a walking crew.',
    slots: FIELD_TEAM_SLOTS,
  },
  {
    id: 'full_bench',
    name: 'Full Bench',
    blurb: 'Everything above, plus texting and numbers.',
    slots: FULL_BENCH_SLOTS,
  },
];

// ---------------------------------------------------------------------------
// Turf
// ---------------------------------------------------------------------------

/**
 * The subregions a `slice` slot may be given: every subregion that sits inside
 * the candidate's reach.
 *
 * An EMPTY reach is the county-wide case — which means everything, not
 * nothing — so every subregion is inside it and the whole catalog is fair
 * game. That is the one place where "empty means everything" works in our
 * favour: a canvasser on a county-wide race still gets one precinct group
 * rather than the county.
 */
export function sliceCatalog(
  reach: UserScopeInput[],
  regions: MapRegionCatalog,
): UserScopeInput[] {
  const keys = reach.length
    ? Array.from(
        new Set(
          reach.flatMap((s) =>
            s.kind === 'subregion'
              ? // Already as narrow as the reach goes — reuse it verbatim.
                []
              : regions.keys.filter(
                  (k) => String(regions.byKey[k].ld) === String(s.ld),
                ),
          ),
        ),
      )
    : regions.keys;

  const fromKeys: UserScopeInput[] = keys.map((k) => ({
    kind: 'subregion',
    ld: String(regions.byKey[k].ld),
    // Already strings on the wire; the catalog keeps that copy so no write
    // path has to re-coerce and risk sending ints (a 422 on UserScopeInput).
    precincts: regions.byKey[k].precinctsAsStrings,
  }));

  // Reach rows that are already subregions pass straight through: they are
  // the narrowest slice available and they are trivially inside the reach.
  const explicit = reach.filter((s) => s.kind === 'subregion').map(cloneScope);

  // A reach of "LD 12 plus the Riverside subregion" would otherwise offer
  // Riverside twice and hand two canvassers the same doors.
  const seen = new Set<string>();
  return [...explicit, ...fromKeys].filter((s) => {
    const key = `${s.ld}:${(s.precincts ?? []).join(',')}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function cloneScope(scope: UserScopeInput): UserScopeInput {
  return {
    kind: scope.kind,
    ld: String(scope.ld),
    ...(scope.precincts ? { precincts: scope.precincts.map(String) } : {}),
  };
}

// ---------------------------------------------------------------------------
// Seats
// ---------------------------------------------------------------------------

/**
 * Role → seat class, used ONLY when the server didn't say.
 *
 * `/admin/seats` returns `role_classes` precisely so this grouping doesn't get
 * duplicated in the frontend and drift from `services/seats.py`. This mirror
 * exists for the older-server case; like the backend it falls back to the
 * costliest class for an unknown role, so a role nobody classified bumps into
 * a limit and gets noticed instead of quietly riding free.
 */
const FALLBACK_ROLE_CLASSES: Record<string, string> = {
  admin: 'admin',
  org_manager: 'admin',
  manager: 'admin',
  analyst: 'staff',
  sms_manager: 'staff',
  candidate: 'staff',
  coordinator: 'staff',
  canvasser: 'field',
  messenger: 'field',
  viewer: 'field',
};

function seatClassFor(role: string, snapshot: SeatSnapshot | null): string {
  return snapshot?.role_classes?.[role] ?? FALLBACK_ROLE_CLASSES[role] ?? 'admin';
}

export interface SeatShortfall {
  seatClass: string;
  /** Rows in the roster that want this class. */
  needed: number;
  /** Seats the tenant has left. Never null here — unlimited classes are omitted. */
  remaining: number;
  /** How many rows over the line, i.e. `needed - remaining`. */
  over: number;
}

/**
 * Which seat classes this roster would overrun.
 *
 * Every roster row is a NEW account, so the comparison is against `remaining`
 * rather than `limit`. Unlimited classes are dropped instead of being compared
 * against a sentinel — `remaining` is null there, and null comparisons are how
 * a UI ends up believing it has minus-one seats.
 */
export function seatShortfalls(
  roster: LaunchRosterMember[],
  snapshot: SeatSnapshot | null,
): SeatShortfall[] {
  if (!snapshot) return [];
  const demand: Record<string, number> = {};
  for (const member of roster) {
    const cls = seatClassFor(member.role, snapshot);
    demand[cls] = (demand[cls] ?? 0) + 1;
  }
  const out: SeatShortfall[] = [];
  for (const [seatClass, needed] of Object.entries(demand)) {
    const usage = snapshot.usage?.[seatClass];
    if (!usage || usage.unlimited || usage.remaining == null) continue;
    if (needed > usage.remaining) {
      out.push({
        seatClass,
        needed,
        remaining: usage.remaining,
        over: needed - usage.remaining,
      });
    }
  }
  return out.sort((a, b) => b.over - a.over);
}

// ---------------------------------------------------------------------------
// Applying a template
// ---------------------------------------------------------------------------

export type SkipReason = 'not_grantable' | 'candidate_exists';

export interface SkippedSlot {
  label: string;
  role: string;
  count: number;
  reason: SkipReason;
}

export interface TemplateApplication {
  /** The roster to commit: what was already there, then the seeded rows. */
  roster: LaunchRosterMember[];
  /** Just the seeded rows, for "added 7 people". */
  added: LaunchRosterMember[];
  /** Slots that did not make it, and why — the UI must say this out loud. */
  skipped: SkippedSlot[];
  /** Seat classes the RESULTING roster would overrun. Empty means it fits. */
  shortfalls: SeatShortfall[];
}

export interface ApplyOptions {
  template: RosterTemplate;
  /** The roster as it stands. Never modified — templates APPEND. */
  roster: LaunchRosterMember[];
  /** The campaign's reach, from step 2. Every seeded scope derives from it. */
  reach: UserScopeInput[];
  /** `/admin/users/role-options`. An empty list filters everything out. */
  roleOptions: RoleOption[];
  /** `/admin/seats`. Null (unreadable, older server) means no seat warning. */
  seats?: SeatSnapshot | null;
  /**
   * This tenant's turfs. An empty catalog is legitimate — a tenant whose
   * turfs are not loaded yet — and degrades every `slice` slot to a copy of
   * the reach rather than inventing a turf.
   */
  regions: MapRegionCatalog;
}

/**
 * Resolve a slot's role against what this provisioner may actually grant.
 * Returns null when neither the role nor any documented alias is available.
 */
function resolveRole(slot: TemplateSlot, grantable: Set<string>): string | null {
  for (const role of [slot.role, ...(slot.fallbackRoles ?? [])]) {
    if (grantable.has(role)) return role;
  }
  return null;
}

/**
 * Build the rows a template would add.
 *
 * **Appends. Never replaces.** A roster that already has rows has hand-typed
 * emails in it, and there is no undo in this wizard — a "replace" that eats
 * three addresses someone just read off a phone is a worse failure than a
 * duplicate row, which is one click to remove. The single exception is the
 * candidate slot: the draft models exactly one `relation: 'candidate'`, so if
 * one is already present the template's candidate is skipped rather than
 * seeding a second one that preflight would reject.
 *
 * Pure: takes the current roster, returns a new one. The caller decides
 * whether to commit it, which is what lets the UI preview a template's cost
 * (rows added, roles dropped, seats overrun) before anyone clicks.
 */
export function applyRosterTemplate(opts: ApplyOptions): TemplateApplication {
  const { template, roster, reach, roleOptions, seats = null, regions } = opts;

  const grantable = new Set(
    roleOptions.filter((o) => o.grantable).map((o) => o.value),
  );
  const hasCandidate = roster.some((m) => m.relation === 'candidate');
  const slices = sliceCatalog(reach, regions);

  const added: LaunchRosterMember[] = [];
  const skipped: SkippedSlot[] = [];
  // One cursor for the whole application, so the organizers and the canvassers
  // spread across the district instead of stacking on subregion #1.
  let sliceCursor = 0;

  for (const slot of template.slots) {
    if (slot.relation === 'candidate' && hasCandidate) {
      skipped.push({
        label: slot.label,
        role: slot.role,
        count: slot.count,
        reason: 'candidate_exists',
      });
      continue;
    }

    const role = resolveRole(slot, grantable);
    if (!role) {
      skipped.push({
        label: slot.label,
        role: slot.role,
        count: slot.count,
        reason: 'not_grantable',
      });
      continue;
    }

    for (let n = 0; n < slot.count; n += 1) {
      let scopes: UserScopeInput[] = [];
      if (slot.relation === 'staff') {
        if (slot.scope === 'slice' && slices.length) {
          scopes = [cloneScope(slices[sliceCursor % slices.length])];
          sliceCursor += 1;
        } else {
          // 'campaign', or a slice with nothing to slice (no turfs loaded for
          // this tenant yet). Copying the reach is the widest thing that is
          // still guaranteed to be a subset of it.
          scopes = reach.map(cloneScope);
        }
      }
      added.push({
        // Emails are typed by a human, always. See the module note.
        email: '',
        full_name: '',
        role,
        relation: slot.relation,
        scopes,
        can_provision: slot.canProvision,
      });
    }
  }

  const next = [...roster, ...added];
  return { roster: next, added, skipped, shortfalls: seatShortfalls(next, seats) };
}

/**
 * "2 more field seats and 1 more admin seat".
 *
 * A roster usually overruns two classes at once — a big team is short on field
 * AND staff seats — so the warning names every class rather than the worst
 * one. Being told to cut canvassers when the coordinators are also over the
 * line just produces a second surprise.
 */
export function describeShortfalls(shortfalls: SeatShortfall[]): string {
  return shortfalls
    .map((s) => `${s.over} more ${s.seatClass} seat${s.over === 1 ? '' : 's'}`)
    .join(' and ');
}
